<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >

    <template v-slot:activator="{ props }">
      <div v-bind="props" >
        <slot></slot>
      </div>
    </template>

    <v-form ref="form" v-bind:disabled="!getUser || !getDBUser" v-on:submit.prevent="submitForm" data-cy="----form">
    <v-card v-cloak >
      <v-card-title class="d-flex justify-space-between">
        {{$t('menus.authentication.enableMFA')}}

        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text scrollable class="scrollable mt-4">

            <v-row no-gutters>
              <p style="line-height: 1.4;">
                {{$t('menus.authentication.textDescription1-1')}}
                {{$t('menus.authentication.textDescription1-2')}}
                <a href="https://authy.com/" target="_blank" style="display: inline; font-size: unset !important;">Authy.</a>
              </p>
            </v-row>

            <div class="d-flex justify-center mt-4 mb-4">
              <MFAQRCode v-model:totpCode="totpCode" v-model:user="getUser">
              </MFAQRCode>
            </div>

            <div class="d-flex">
              <VTextField
                  v-bind:model-value="totpCode"
                  v-bind:label="$t('menus.authentication.secretKey')"
                  readonly
                  variant="solo-filled" flat
                  block
              >

                <template v-slot:append>
                  <v-tooltip location="bottom" z-index="1200">
                    <template v-slot:activator="{props}">
                      <v-btn variant="flat" icon v-bind="props" v-on:click="copyToClipboard(totpCode)">
                        <v-icon>mdi-clipboard-file-outline</v-icon>
                      </v-btn>
                    </template>

                    <span>{{$t('menus.authentication.copyToClipboard')}}</span>
                  </v-tooltip>
                </template>

              </VTextField>
            </div>

            <v-row no-gutters>
              <p style="line-height: 1.4;">
                {{$t('menus.authentication.textDescription2')}}
              </p>
            </v-row>

            <div class="mt-4">
              <v-text-field
                  v-model="oneTimeCode"
                  v-bind:label="$t('menus.authentication.oneTimeCode')"
                  v-bind:rules="oneTimeCodeRules"
                  prepend-inner-icon="mdi-timer-lock-outline"
                  variant="solo-filled" flat
                  block
                  autocomplete="off"
              >
              </v-text-field>
            </div>

            <div v-if="successMessage" class="bg-success" data-cy="settings-authentication-successMessage">
              {{successMessage}}
            </div>

            <div v-if="errorMessage" class="bg-error" data-cy="settings-authentication-errorMessage">
              {{errorMessage}}
            </div>

      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn type="submit" class="bg-primary" elevation=0 v-bind:loading="isSubmitted" v-bind:disabled="isSubmitted" >
          {{$t('menus.authentication.enableMFA')}}
        </v-btn>
        <v-btn variant="text" @click="dialog = false">{{ $t('common.close') }}</v-btn>
      </v-card-actions>
    </v-card>
    </v-form>


  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore'
import MFAQRCode from "@/routes/account/components/MFAQRCode.vue";
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import UserCalls from '@/api/modules/UserCalls.js';

export default {
  emits: ["update:is-processing"],
  mixins: [DialogActionKeys],
  name: "AccountEnableMFA",

  components: {
    MFAQRCode,
  },

  props: {
    isProcessing: {
      type: Boolean,
      default: false,
    },
  },

  data: function() {
    return {
      dialog: false,
      errorMessage: '',
      successMessage: '',

      totpCode: '',
      oneTimeCode: '',
      oneTimeCodeRules: [
        v => !!v || this.$t('menus.authentication.oneTimeCodeRequired'),
        v => /^\d{6}$/.test(v) || this.$t('menus.authentication.oneTimeCodeInvalid')
      ],

      isSubmitted: false,
    };
  },

  watch: {
    dialog: function(value) {
      if (!value) {
        this.reset();
      }
    },
  },

  mounted: function() {
    this.generateTOTPCode();

    const authStore = useAuthStore();
    authStore.refreshCognitoUser();

    // authStore.refreshDBUser();
  },

  methods: {
    generateTOTPCode: function() {
      const authStore = useAuthStore();
      authStore.setupTOTP().then((totp) => {
        this.totpCode = totp.sharedSecret;
      });
    },
    copyToClipboard: function(url) {
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(url)
      } else {
        // it works only on localhost and hosts with https
        // console.log("Copy not supported")
      }
    },
    submitForm: async function() {
      this.errorMessage = "";
      this.successMessage = "";

      const { valid } = await this.$refs.form.validate(); // Ensure validation is awaited if it's async
      if (!valid) { // Use 'valid' property if validate returns an object
        return;
      }

      this.isSubmitted = true;

      const payload = {
        code: this.oneTimeCode,
      };

      const authStore = useAuthStore();

      try {
        await authStore.verifyTOTPToken(payload);
        await authStore.enableTOTPMFA();
        await UserCalls.invalidateMfaCache().catch(console.error);
        this.refreshUserWithTimeout(); // This function still uses setTimeout, but the calls before it are awaited

      } catch (error) {
        this.isSubmitted = false;

        if (error.code === 'EnableSoftwareTokenMFAException') {
          this.errorMessage = this.$t('menus.authentication.oneTimeCodeInvalid');
        } else {
          if (error && error.message) {
            this.errorMessage = error.message;
          } else {
            this.errorMessage = "Error!";
          }
        }
      }
    },
    refreshUserWithTimeout: function() {
      this.$emit('update:is-processing', true);

      let self = this;

      setTimeout(() => {
        const authStore = useAuthStore();
        authStore.refreshCognitoUser();
        self.$emit('update:is-processing', false);

        self.dialog = false;
        self.$showMessage(self.$t('menus.authentication.successfullyEnabled'));
      }, 10000);
    },
    reset() {
      this.successMessage = '';
      this.errorMessage = '';
      this.oneTimeCode = '';
    }
  },

  computed: {
    getDBUser: function() {
      const authStore = useAuthStore();
      return authStore.dbUser;
    },
    getUser: function() {
      const authStore = useAuthStore();
      return authStore.user;
    },
  }
};
</script>
